import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import bookingService from '../services/bookingService';

export const useBookingsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['bookings', params],
    queryFn: () => bookingService.getBookings(params),
    ...options,
  });
};

export const usePendingBookingsQuery = (page = 1, limit = 20, options = {}) => {
  return useQuery({
    queryKey: ['bookings', 'pending', { page, limit }],
    queryFn: () => bookingService.getPendingAssistantBookings(page, limit),
    ...options,
  });
};

export const useMyBookingsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['bookings', 'mine', params],
    queryFn: () => bookingService.getMyBookings(params),
    ...options,
  });
};

export const useCancelBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.cancelBooking(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};

export const useBookingByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: ['bookings', id],
    queryFn: () => bookingService.getBookingById(id),
    enabled: !!id,
    ...options,
  });
};

export const useInitPaystackPaymentMutation = () => {
  return useMutation({
    mutationFn: (payload) => bookingService.initPaystackPayment(payload),
  });
};

export const useAcceptBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.acceptBooking(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};

export const useRejectBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.rejectBooking(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};

export const useAssistantArrivalMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.assistantArrival(id, payload),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', id] });
    },
  });
};

export const useStartTripMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.startTrip(id, payload),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', id] });
    },
  });
};

// The traveller's half of the two-party handshake. Invalidating 'bookings'
// covers both the My Trips list and the action bar in the header, so a
// confirmation from either place updates the other.
export const useConfirmTripStartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.confirmTripStart(id, payload),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', id] });
    },
  });
};

export const useConfirmTripEndMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.confirmTripEnd(id, payload),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', id] });
    },
  });
};

export const useCompleteTripMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload = {} }) => bookingService.completeTrip(id, payload),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', id] });
      queryClient.invalidateQueries({ queryKey: ['profile'] }); // Invalidate profile to refresh wallet balances
    },
  });
};
